import { useCallback } from "react";
import { announce } from "@react-aria/live-announcer";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { useIsEditForm } from "@/widgets/classic-form/IsEditFormContext";
import { useFormAPI } from "@/widgets/classic-form/form";
import { useDefaultFormValues } from "@/widgets/classic-form/form-control/useDefaultFormValues";
import { useMetadataQuery } from "@/entities/form-metadata";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { logFCI } from "@/shared/lib/fci/logFCI";
import { applyPersonToForm } from "./applyPersonToForm";
import { buildResetFormValues } from "./buildResetFormValues";
import { getSuggestionLabel } from "./getSuggestionLabel";

// Re-exported from its new home to keep existing import sites/tests stable.
export { ensureNonRemovableDefaults } from "./applyPersonToForm";

export const useQuickFillSelection = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const formApi = useFormAPI();
    const { data: formMetadata } = useMetadataQuery();
    const isEditForm = useIsEditForm();
    const defaultFormValues = useDefaultFormValues({ isEditForm });

    const selectSuggestion = useCallback(
        async (suggestion: PersonSuggestionItem, treeId: string) => {
            const currentPersonId = formApi.getFieldValue("treePerson").personId;
            const label = getSuggestionLabel(suggestion);

            // Deselect: clicking the already-selected person
            if (currentPersonId === suggestion.PersonId) {
                announce(t("suggestions.suggestionUnselected", { label }), "polite");
                const resetValues = buildResetFormValues(defaultFormValues, formApi.getValues(), {
                    preserveName: true,
                });
                formApi.setValues(resetValues);
                return;
            }

            // Select or re-select
            try {
                await applyPersonToForm({
                    personId: suggestion.PersonId,
                    treeId,
                    queryClient,
                    formApi,
                    formMetadata,
                    defaultFormValues,
                });
                announce(t("suggestions.suggestionSelected", { label }), "polite");
            } catch (error) {
                announce(t("suggestions.suggestionSelectionFailed"), "assertive");
                logFCI({ errorType: FCI_ERROR.Convenience, errorInfo: `QuickFill selection failed: ${error}` });
            }
        },
        [formApi, formMetadata, defaultFormValues, queryClient, t],
    );

    return { selectSuggestion };
};
